/**
 * Station operating hours, read-only.
 *
 * ant stores a station's hours in `location.operating_hours` as
 * `{ "v1": { "0": [{ "open": s, "closed": s }], ..., "6": [...] } }`, keyed by UTC
 * day (0 = Sunday), with `s` = seconds of that UTC day. ant only checks them when a
 * rental starts and the system's `operating_hours` flag (Settings › Rentals ›
 * "Time Restrictions") is on; a station whose value is missing (nil) uses the
 * system's open/close times instead, and one set to anything that isn't a map gets
 * no time limit at all. Everything is compared in UTC with no daylight saving.
 *
 * The rules below mirror `outside_open_hours?/2`, `station_open?/2` and
 * `outside_window?/3` in ant's lib/ant/rentals/rental/actions/start.ex.
 */

export const SECONDS_PER_DAY = 86400;
const SECONDS_PER_WEEK = 7 * SECONDS_PER_DAY;

export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** One stored window, read the way ant's `to_number/1` reads it. */
export interface RawHoursWindow {
  open: number | null;
  closed: number | null;
  /**
   * ant can't read this entry and raises when it reaches it, so the rental start
   * fails: a window that isn't a map or null, a day that isn't a list (or null,
   * false, {}), or a `v1` that isn't a map (or null).
   */
  unreadable?: boolean;
}

/** Station windows by UTC day: index 0 = Sunday ... 6 = Saturday. */
export type UtcWeekHours = RawHoursWindow[][];

/** A stretch of a day when rentals can start, in seconds (0..86400). */
export interface DayInterval {
  start: number;
  end: number;
}

/** A window on a local day; when `endsNextDay`, `end` is a time of the next day. */
export interface LocalWindow extends DayInterval {
  endsNextDay: boolean;
}

export type LocalDaySchedule =
  | { kind: 'allDay' }
  | { kind: 'closed' }
  | { kind: 'windows'; windows: LocalWindow[] };

export interface TimeZoneInfo {
  /** The zone the times are shown in ('UTC' when the system's is missing/unknown). */
  timeZone: string;
  /** Current UTC offset of the zone, in seconds (EDT = -14400). */
  offsetSeconds: number;
  /** e.g. 'EDT', 'GMT', 'GMT+1' */
  abbreviation: string;
  /** e.g. 'UTC-4', 'UTC+5:30', 'UTC' */
  offsetLabel: string;
  /** false when we fell back to UTC */
  valid: boolean;
}

// ============================================================================
// Parsing (mirrors ant's to_number/1 and get_in(station_hours, ["v1", day]))
// ============================================================================

const LEADING_INTEGER = /^[+-]?\d+/;

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : null;
  }
  if (typeof value === 'string') {
    // Integer.parse/1: leading integer, the rest is ignored
    const match = value.match(LEADING_INTEGER);
    return match ? parseInt(match[0], 10) : null;
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const isNil = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

const UNREADABLE_WINDOW: RawHoursWindow = { open: null, closed: null, unreadable: true };

/**
 * One UTC day's entry, read like ant's
 * `Enum.reduce_while(get_in(...) || [], ...)` with `hours["open"]` on each item.
 */
function parseDay(windows: unknown): RawHoursWindow[] {
  // `|| []`: nil and false are an empty day; Enum over an empty map is one too.
  if (isNil(windows) || windows === false) return [];
  if (isRecord(windows)) return Object.keys(windows).length ? [UNREADABLE_WINDOW] : [];
  // true, a string or a number: Enumerable isn't implemented, ant raises.
  if (!Array.isArray(windows)) return [UNREADABLE_WINDOW];

  return windows.map((window) => {
    // nil["open"] is nil: a window that doesn't restrict.
    if (isNil(window)) return { open: null, closed: null };
    // ["open"] on a string, number, boolean or list raises in Access.get/3.
    if (!isRecord(window)) return UNREADABLE_WINDOW;
    return { open: toNumber(window.open), closed: toNumber(window.closed) };
  });
}

/**
 * Read the station's `operatingHours` Json into its seven UTC days. Returns null
 * when the value isn't a map: a missing value (ant falls back to the system's
 * window) and a non-map one (ant enforces no limit) are told apart by the caller.
 * A map whose days are missing or empty is still a map: those days are
 * unrestricted. Entries ant raises on are kept as `unreadable` windows.
 */
export function parseStationHours(value: unknown): UtcWeekHours | null {
  let hours = value;
  if (typeof hours === 'string') {
    try {
      hours = JSON.parse(hours);
    } catch {
      return null;
    }
  }
  if (!isRecord(hours)) return null;

  const { v1 } = hours;
  // get_in(nil, [day]) is nil; on a list, string, number or boolean it raises.
  if (isNil(v1)) return DAY_NAMES.map(() => []);
  if (!isRecord(v1)) return DAY_NAMES.map(() => [UNREADABLE_WINDOW]);
  return DAY_NAMES.map((_, day) => parseDay(v1[String(day)]));
}

/** The system's open/close times, applied to every UTC day (ant: outside_window?/3). */
export function systemWindowAsWeek(
  openTimeInt: number | null | undefined,
  closeTimeInt: number | null | undefined
): UtcWeekHours | null {
  if (openTimeInt === null || openTimeInt === undefined) return null;
  if (closeTimeInt === null || closeTimeInt === undefined) return null;
  return DAY_NAMES.map(() => [{ open: openTimeInt, closed: closeTimeInt }]);
}

// ============================================================================
// Conversion
// ============================================================================

function mergeIntervals(intervals: DayInterval[]): DayInterval[] {
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const merged: DayInterval[] = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }
  return merged;
}

const isIncomplete = (w: RawHoursWindow) =>
  w.open === null || w.closed === null || w.open === w.closed;

/**
 * When rentals can start during one UTC day, mirroring ant's `station_open?/2`:
 * - no windows: the day is unrestricted;
 * - open < closed: open between the two;
 * - open > closed: open from 00:00 until `closed` AND from `open` until 24:00 of the
 *   same UTC day (each UTC day is checked against its own entry only);
 * - a window missing a time or with open == closed doesn't restrict, and when it is
 *   the LAST window of the day the whole day is unrestricted (ant's reduce ends on
 *   that window's `true`);
 * - an unreadable entry makes ant raise when its reduce gets there, which only a
 *   window before it that contains the time avoids: the day is open only in the
 *   windows before the first unreadable entry, and the start fails at any other time.
 */
export function utcDayOpenIntervals(windows: RawHoursWindow[]): DayInterval[] {
  const firstUnreadable = windows.findIndex((w) => w.unreadable);
  const readable = firstUnreadable === -1 ? windows : windows.slice(0, firstUnreadable);
  if (
    firstUnreadable === -1 &&
    (windows.length === 0 || isIncomplete(windows[windows.length - 1]))
  ) {
    return [{ start: 0, end: SECONDS_PER_DAY }];
  }

  const intervals: DayInterval[] = [];
  for (const { open, closed } of readable) {
    if (open === null || closed === null || open === closed) continue;
    if (open < closed) {
      intervals.push({ start: open, end: closed });
    } else {
      intervals.push({ start: 0, end: closed }, { start: open, end: SECONDS_PER_DAY });
    }
  }

  return mergeIntervals(
    intervals
      .map(({ start, end }) => ({
        start: Math.max(0, start),
        end: Math.min(SECONDS_PER_DAY, end),
      }))
      .filter(({ start, end }) => start < end)
  );
}

/**
 * Convert the UTC-day hours to local days (index 0 = local Sunday) for a fixed UTC
 * offset. Every open stretch is moved by the offset and split at local midnight, so
 * it lands on the local day(s) it actually falls on. A window that runs past local
 * midnight into the next day's first window is shown once, on the day it opens,
 * as "… (next day)".
 */
export function toLocalWeek(
  hours: UtcWeekHours,
  offsetSeconds: number
): LocalDaySchedule[] {
  const buckets: DayInterval[][] = DAY_NAMES.map(() => []);

  hours.forEach((windows, utcDay) => {
    for (const interval of utcDayOpenIntervals(windows)) {
      let start = utcDay * SECONDS_PER_DAY + interval.start + offsetSeconds;
      let end = utcDay * SECONDS_PER_DAY + interval.end + offsetSeconds;
      const weeks = Math.floor(start / SECONDS_PER_WEEK);
      start -= weeks * SECONDS_PER_WEEK;
      end -= weeks * SECONDS_PER_WEEK;

      while (start < end) {
        const day = Math.floor(start / SECONDS_PER_DAY);
        const segmentEnd = Math.min(end, (day + 1) * SECONDS_PER_DAY);
        buckets[day % 7].push({
          start: start - day * SECONDS_PER_DAY,
          end: segmentEnd - day * SECONDS_PER_DAY,
        });
        start = segmentEnd;
      }
    }
  });

  const days: LocalDaySchedule[] = buckets.map((bucket) => {
    const merged = mergeIntervals(bucket);
    if (merged.length === 0) return { kind: 'closed' };
    if (
      merged.length === 1 &&
      merged[0].start === 0 &&
      merged[0].end === SECONDS_PER_DAY
    ) {
      return { kind: 'allDay' };
    }
    return {
      kind: 'windows',
      windows: merged.map((w) => ({ ...w, endsNextDay: false })),
    };
  });

  // Join a window that runs until local midnight with the next day's window that
  // starts at midnight: "9:00 PM – 1:00 AM (next day)" on the day it opens.
  days.forEach((_, day) => {
    const today = days[day];
    const tomorrowIndex = (day + 1) % 7;
    const tomorrow = days[tomorrowIndex];
    if (today.kind !== 'windows' || tomorrow.kind !== 'windows') return;

    const last = today.windows[today.windows.length - 1];
    const first = tomorrow.windows[0];
    if (last.end !== SECONDS_PER_DAY || last.endsNextDay || first.start !== 0) return;

    today.windows[today.windows.length - 1] = {
      start: last.start,
      end: first.end,
      endsNextDay: true,
    };
    const rest = tomorrow.windows.slice(1);
    days[tomorrowIndex] = rest.length
      ? { kind: 'windows', windows: rest }
      : { kind: 'closed' };
  });

  return days;
}

// ============================================================================
// Time zone
// ============================================================================

const UTC_FALLBACK: TimeZoneInfo = {
  timeZone: 'UTC',
  offsetSeconds: 0,
  abbreviation: 'UTC',
  offsetLabel: 'UTC',
  valid: false,
};

function formatOffsetLabel(offsetSeconds: number): string {
  if (offsetSeconds === 0) return 'UTC';
  const sign = offsetSeconds < 0 ? '-' : '+';
  const totalMinutes = Math.round(Math.abs(offsetSeconds) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
}

/**
 * The zone's UTC offset and abbreviation at `at` (Intl API). ant ignores DST, so
 * the converted times are only right for the offset in effect right now.
 */
export function getTimeZoneInfo(
  timeZone: string | null | undefined,
  at: Date = new Date()
): TimeZoneInfo {
  if (!timeZone) return UTC_FALLBACK;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      timeZoneName: 'short',
    }).formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? '';

    const wallClockAsUtc = Date.UTC(
      Number(part('year')),
      Number(part('month')) - 1,
      Number(part('day')),
      Number(part('hour')) % 24,
      Number(part('minute')),
      Number(part('second'))
    );
    const instant = Math.floor(at.getTime() / 1000) * 1000;
    const offsetSeconds = Math.round((wallClockAsUtc - instant) / 1000);

    return {
      timeZone,
      offsetSeconds,
      abbreviation: part('timeZoneName') || formatOffsetLabel(offsetSeconds),
      offsetLabel: formatOffsetLabel(offsetSeconds),
      valid: true,
    };
  } catch {
    return UTC_FALLBACK;
  }
}

// ============================================================================
// Formatting
// ============================================================================

/** Seconds of a local day as "7:00 AM" (86400 = midnight = "12:00 AM"). */
export function formatClockTime(seconds: number): string {
  const minutesOfDay = Math.floor(seconds / 60) % (24 * 60);
  const hours = Math.floor(minutesOfDay / 60);
  const minutes = minutesOfDay % 60;
  const period = hours < 12 ? 'AM' : 'PM';
  const hours12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

export function formatLocalDay(day: LocalDaySchedule, abbreviation: string): string {
  if (day.kind === 'allDay') return 'Open all day';
  if (day.kind === 'closed') return 'Closed';
  const windows = day.windows
    .map((w) => `${formatClockTime(w.start)} – ${formatClockTime(w.end)}`)
    .join(', ');
  const nextDay = day.windows[day.windows.length - 1].endsNextDay ? ' (next day)' : '';
  return `${windows} ${abbreviation}${nextDay}`;
}

// ============================================================================
// View model for the location page
// ============================================================================

export interface LocationHoursSystem {
  timezone?: string | null;
  operatingHours?: boolean | null;
  openTimeInt?: number | null;
  closeTimeInt?: number | null;
}

export interface LocationHoursView {
  timeZone: TimeZoneInfo;
  /** The system's Time Restrictions flag; ant ignores all hours when it is off. */
  enforced: boolean;
  /**
   * The station value is set but isn't a map: ant's `outside_open_hours?/2` only
   * uses the system window for a nil value, so no time limit applies.
   */
  unreadable: boolean;
  /** The map has entries ant raises on: the start fails at those times (shown closed). */
  hasUnreadableEntries: boolean;
  /** Sunday..Saturday in the system's zone, or null when the station has no map. */
  stationHours: { day: string; local: string }[] | null;
  /** The system's window (applies when the station value is missing), if it has one. */
  systemHours: { local: string } | null;
}

export function buildLocationHoursView({
  operatingHours,
  system,
  now = new Date(),
}: {
  operatingHours: unknown;
  system?: LocationHoursSystem | null;
  now?: Date;
}): LocationHoursView {
  const timeZone = getTimeZoneInfo(system?.timezone, now);
  const enforced = system?.operatingHours === true;
  const stationWeek = parseStationHours(operatingHours);

  if (stationWeek) {
    const localWeek = toLocalWeek(stationWeek, timeZone.offsetSeconds);
    return {
      timeZone,
      enforced,
      unreadable: false,
      hasUnreadableEntries: stationWeek.some((windows) =>
        windows.some((w) => w.unreadable)
      ),
      stationHours: DAY_NAMES.map((day, index) => ({
        day,
        local: formatLocalDay(localWeek[index], timeZone.abbreviation),
      })),
      systemHours: null,
    };
  }

  // Set but not a map: outside_open_hours?/2 only falls back to the system window
  // for nil and applies no limit to anything else. (ant's own API never sends one:
  // its :map attribute refuses to load a stored non-map.)
  if (!isNil(operatingHours)) {
    return {
      timeZone,
      enforced,
      unreadable: true,
      hasUnreadableEntries: false,
      stationHours: null,
      systemHours: null,
    };
  }

  const systemWeek = systemWindowAsWeek(system?.openTimeInt, system?.closeTimeInt);
  return {
    timeZone,
    enforced,
    unreadable: false,
    hasUnreadableEntries: false,
    stationHours: null,
    // The same window every UTC day converts to the same window every local day.
    systemHours: systemWeek
      ? {
          local: formatLocalDay(
            toLocalWeek(systemWeek, timeZone.offsetSeconds)[0],
            timeZone.abbreviation
          ),
        }
      : null,
  };
}
